import { useState } from "react";

function SecuritySettings({ showNotification }) {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState({});
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validatePasswordForm = () => {
    const newErrors = {};
    
    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = "La contraseña actual es obligatoria";
    }
    
    if (!passwordForm.newPassword) {
      newErrors.newPassword = "La nueva contraseña es obligatoria";
    } else if (passwordForm.newPassword.length < 8) {
      newErrors.newPassword = "La contraseña debe tener al menos 8 caracteres";
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = "Las contraseñas no coinciden";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    
    if (validatePasswordForm()) {
      // Here you would normally send to backend
      showNotification("Contraseña actualizada correctamente");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    }
  };

  const toggleTwoFactor = () => {
    // In a real app, this would involve backend calls and QR code display for setup
    setTwoFactorEnabled(!twoFactorEnabled);
    showNotification(twoFactorEnabled 
      ? "Autenticación de dos factores desactivada"
      : "Autenticación de dos factores activada"
    );
  };

  const handleLogoutOtherSessions = () => {
    // In a real app, this would involve a backend call
    showNotification("Todas las otras sesiones han sido cerradas");
  };

  return (
    <div className="p-6 space-y-8">
      {/* Password Change Section */}
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Cambiar Contraseña</h3>
        <form className="space-y-4 max-w-md" onSubmit={handlePasswordSubmit}>
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-400 mb-1">
              Contraseña Actual
            </label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              className={`w-full px-3 py-2 bg-[#252525] border ${
                errors.currentPassword ? "border-red-500" : "border-[#3a3a3a]"
              } rounded-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]`}
            />
            {errors.currentPassword && <p className="mt-1 text-sm text-red-500">{errors.currentPassword}</p>}
          </div>
          
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-400 mb-1">
              Nueva Contraseña
            </label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              className={`w-full px-3 py-2 bg-[#252525] border ${
                errors.newPassword ? "border-red-500" : "border-[#3a3a3a]"
              } rounded-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]`}
            />
            {errors.newPassword && <p className="mt-1 text-sm text-red-500">{errors.newPassword}</p>}
          </div>
          
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-400 mb-1">
              Confirmar Contraseña
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              className={`w-full px-3 py-2 bg-[#252525] border ${
                errors.confirmPassword ? "border-red-500" : "border-[#3a3a3a]"
              } rounded-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]`}
            />
            {errors.confirmPassword && <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>}
          </div>
          
          <div>
            <button
              type="submit"
              className="px-4 py-2 bg-[#ff1b6d] text-white rounded-md hover:bg-[#ff1b6d]/90 focus:outline-none focus:ring-2 focus:ring-[#ff1b6d]/50"
            >
              Actualizar Contraseña
            </button>
          </div>
        </form>
      </div>

      {/* Two-Factor Authentication */}
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Autenticación de Dos Factores</h3>
        <div className="flex items-center justify-between p-4 bg-[#252525] rounded-lg border border-[#3a3a3a]">
          <div>
            <div className="font-medium text-white">Protege tu cuenta con 2FA</div>
            <div className="text-sm text-gray-400">Añade una capa extra de seguridad a tu cuenta.</div>
          </div>
          <div className="flex items-center">
            <button
              type="button"
              className="px-3 py-1.5 text-sm bg-transparent border border-[#ff1b6d] text-[#ff1b6d] rounded-md hover:bg-[#ff1b6d]/10 focus:outline-none focus:ring-2 focus:ring-[#ff1b6d]/50"
              onClick={toggleTwoFactor}
            >
              {twoFactorEnabled ? "Desactivar" : "Activar"}
            </button>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Sesiones Activas</h3>
        <div className="p-4 bg-[#252525] rounded-lg border border-[#3a3a3a]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-medium text-white">Este dispositivo</div>
              <div className="text-sm text-gray-400">Madrid, España • Última actividad ahora mismo</div>
            </div>
            <div className="text-sm text-green-500 flex items-center">
              <span className="h-2 w-2 rounded-full bg-green-500 mr-2"></span>
              Activo
            </div>
          </div>
          <button
            type="button"
            className="text-sm text-red-500 hover:text-red-400 focus:outline-none"
            onClick={handleLogoutOtherSessions}
          >
            Cerrar todas las otras sesiones
          </button>
        </div>
      </div>
    </div>
  );
}

export default SecuritySettings;
